import React, { Component } from "react";
import { Button } from "reactstrap";

/**
 * Catches render/lifecycle errors from the routed page so one bad component
 * can't take the whole app down.
 *
 * Why this matters here: React unmounts the entire tree on an uncaught render
 * error, leaving a blank or frozen page with nothing in the UI to explain it.
 * That has already cost real debugging time on this project — a crash thrown
 * from one button made an unrelated page look like its buttons "didn't work".
 * Evaluators are mid-report when this happens, so the message is in Spanish and
 * says plainly that their saved work is safe.
 *
 * Note this only catches errors thrown while rendering. It does NOT catch
 * failures inside async handlers (a rejected save mutation, for example) — those
 * still need their own try/catch and user-facing error, as the Conclusiones save
 * now does.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the real stack in the console so it's recoverable from a screenshot
    // or a screen-share when a user reports "the page went blank".
    console.error("Error no manejado en la página:", error, errorInfo);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ padding: "2rem", maxWidth: "40rem", margin: "0 auto", textAlign: "center" }}>
        <h4 style={{ color: "#21497e" }}>Algo salió mal en esta página</h4>
        <p style={{ marginTop: "1rem" }}>
          Ocurrió un error inesperado al mostrar esta sección.{" "}
          <strong>La información que ya había guardado no se perdió.</strong>
        </p>
        <p>
          Puede volver a cargar la página para continuar. Si el problema sigue
          ocurriendo, avise a soporte e incluya el nombre de la sección donde pasó.
        </p>
        <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.75rem", justifyContent: "center" }}>
          <Button color="primary" onClick={() => window.location.reload()}>
            Recargar la página
          </Button>
          {/* Full reload rather than a router push: after a crash the component
              tree is already unmounted, so a soft navigation can land back in the
              same broken state. */}
          <Button color="secondary" onClick={() => window.location.assign("/dashboard")}>
            Volver al inicio
          </Button>
        </div>
        <details style={{ marginTop: "2rem", textAlign: "left", color: "#666" }}>
          <summary style={{ cursor: "pointer" }}>Detalles técnicos (para soporte)</summary>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.8rem", marginTop: "0.5rem" }}>
            {String(error && (error.stack || error.message || error))}
          </pre>
        </details>
      </div>
    );
  }
}

export default ErrorBoundary;
